/**
 * AdminRealTimeMetrics - Real-time KPI Dashboard
 * Shows leads/hour, conversion rates, vendor rankings
 */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  Clock,
  Zap,
  Award,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Activity,
  ArrowUp,
  ArrowDown,
  Minus
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Animated number counter
const AnimatedNumber = ({ value, suffix = "", prefix = "" }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const duration = 500;
    const steps = 20;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return <span>{prefix}{displayValue}{suffix}</span>;
};

// Bar chart for leads per hour
const LeadsPerHourChart = ({ data }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end justify-between gap-1 h-32">
      {data.map((item, idx) => (
        <div key={idx} className="flex flex-col items-center flex-1 group">
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: `${Math.max((item.count / maxCount) * 100, 4)}%` }}
            transition={{ delay: idx * 0.03, duration: 0.4 }}
            className={`w-full rounded-t transition-colors ${
              item.is_current 
                ? 'bg-gradient-to-t from-emerald-600 to-emerald-400' 
                : 'bg-gradient-to-t from-blue-600/80 to-blue-400/80 hover:from-blue-600 hover:to-blue-400'
            }`}
          >
            <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
              {item.count} leads
            </div>
          </motion.div>
          <span className={`text-[10px] mt-1 ${item.is_current ? 'text-emerald-600 font-bold' : 'text-navy-400'}`}>
            {item.hour.split(':')[0]}
          </span>
        </div>
      ))}
    </div>
  );
};

// Conversion funnel visualization
const ConversionFunnel = ({ funnel }) => {
  const colors = ['bg-blue-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-green-600'];
  
  return (
    <div className="space-y-2">
      {funnel.map((stage, idx) => (
        <div key={stage.stage} className="relative">
          <div className="flex items-center gap-3">
            <div className="w-24 text-xs text-navy-600 text-right">{stage.stage}</div>
            <div className="flex-1 h-8 bg-navy-100 rounded-lg overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stage.rate}%` }}
                transition={{ delay: idx * 0.15, duration: 0.5 }}
                className={`h-full ${colors[idx]} flex items-center justify-end px-2`}
              >
                <span className="text-xs font-bold text-white">
                  {stage.count}
                </span>
              </motion.div>
            </div>
            <div className="w-12 text-xs font-semibold text-navy-700">
              {stage.rate}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Vendor ranking item
const VendorRankItem = ({ vendor, rank }) => {
  const getRankStyle = (r) => {
    if (r === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
    if (r === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
    if (r === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return 'bg-navy-200 text-navy-600';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.05 }}
      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-navy-100 hover:border-navy-200 transition-colors"
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${getRankStyle(rank)}`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-navy-800 truncate">{vendor.name}</p>
        <p className="text-xs text-navy-500">
          {vendor.leads_claimed} tomados - {vendor.leads_converted} convertidos
        </p>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-emerald-600">{vendor.conversion_rate}%</p>
        <p className="text-xs text-navy-400">conversion</p>
      </div>
    </motion.div>
  );
};

// Metric card component
const MetricCard = ({ icon: Icon, title, value, subtitle, trend, trendValue, color = "blue", size = "normal" }) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    green: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    purple: "from-purple-500 to-purple-600",
    red: "from-red-500 to-red-600",
    cyan: "from-cyan-500 to-cyan-600"
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl border border-navy-100 overflow-hidden ${size === 'large' ? 'p-6' : 'p-4'}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-500' : 'text-navy-400'
          }`}>
            {trend > 0 ? <ArrowUp className="w-3 h-3" /> : trend < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            {Math.abs(trendValue || trend)}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className={`font-bold text-navy-800 ${size === 'large' ? 'text-3xl' : 'text-2xl'}`}>
          {typeof value === 'number' ? <AnimatedNumber value={value} /> : value}
        </p>
        <p className="text-sm text-navy-500 mt-1">{title}</p>
        {subtitle && <p className="text-xs text-navy-400 mt-0.5">{subtitle}</p>}
      </div>
    </motion.div>
  );
};

const AdminRealTimeMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchMetrics = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem("token");
      
      const [metricsRes, funnelRes] = await Promise.all([
        axios.get(`${API}/api/metrics/realtime`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/metrics/conversion-funnel?days=7`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setMetrics(metricsRes.data);
      setFunnel(funnelRes.data);
      setLastUpdate(new Date());
      
      if (showToast) {
        toast.success("Metricas actualizadas");
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
      if (showToast) {
        toast.error("Error al cargar metricas");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    
    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => fetchMetrics(), 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchMetrics, autoRefresh]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Activity className="w-12 h-12 text-blue-500" />
            </motion.div>
            <p className="text-navy-600">Cargando metricas en tiempo real...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy-800 flex items-center gap-2">
              <Activity className="w-7 h-7 text-blue-500" />
              Metricas en Tiempo Real
            </h1>
            <p className="text-navy-500 text-sm mt-1">
              KPIs de rendimiento actualizados cada 30 segundos
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {lastUpdate && (
              <span className="text-xs text-navy-400">
                Actualizado: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'border-emerald-500 text-emerald-600' : ''}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button
              onClick={() => fetchMetrics(true)}
              disabled={refreshing}
              className="bg-gradient-to-r from-blue-500 to-blue-600"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Main KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            icon={Target}
            title="Leads Hoy"
            value={metrics?.today?.total_leads || 0}
            trend={metrics?.today?.lead_trend}
            trendValue={Math.abs(metrics?.today?.lead_trend || 0)}
            color="blue"
            subtitle={`vs. ${metrics?.today?.vs_yesterday?.leads || 0} ayer`}
          />
          <MetricCard
            icon={CheckCircle2}
            title="Leads Tomados"
            value={metrics?.today?.leads_claimed || 0}
            subtitle={`${metrics?.today?.claim_rate || 0}% del total`}
            color="cyan"
          />
          <MetricCard
            icon={TrendingUp}
            title="Convertidos"
            value={metrics?.today?.leads_converted || 0}
            subtitle={`Tasa: ${metrics?.today?.conversion_rate || 0}%`}
            color="green"
          />
          <MetricCard
            icon={Users}
            title="Vendedores Activos"
            value={metrics?.active_vendors_count || 0}
            subtitle="En los ultimos 30 min"
            color="purple"
          />
        </div>

        {/* Response Metrics & Alerts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            icon={Clock}
            title="Tiempo Promedio de Respuesta"
            value={`${metrics?.response_metrics?.avg_response_time_min || 0} min`}
            subtitle="Desde captura hasta contacto"
            color="amber"
          />
          <MetricCard
            icon={Zap}
            title="Leads Esta Hora"
            value={metrics?.current_hour?.leads || 0}
            subtitle={`Promedio: ${metrics?.current_hour?.avg_per_hour || 0}/hora`}
            trend={metrics?.current_hour?.above_average ? 1 : -1}
            color={metrics?.current_hour?.above_average ? "green" : "blue"}
          />
          <div className={`bg-white rounded-2xl border p-4 ${
            (metrics?.response_metrics?.urgent_leads || 0) > 0 
              ? 'border-red-300 bg-red-50' 
              : 'border-navy-100'
          }`}>
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                (metrics?.response_metrics?.urgent_leads || 0) > 0 
                  ? 'bg-gradient-to-br from-red-500 to-red-600' 
                  : 'bg-gradient-to-br from-navy-400 to-navy-500'
              }`}>
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="mt-3">
              <p className={`text-2xl font-bold ${
                (metrics?.response_metrics?.urgent_leads || 0) > 0 ? 'text-red-600' : 'text-navy-800'
              }`}>
                <AnimatedNumber value={metrics?.response_metrics?.urgent_leads || 0} />
              </p>
              <p className="text-sm text-navy-500 mt-1">Leads Urgentes</p>
              <p className="text-xs text-navy-400 mt-0.5">
                Esperando mas de 10 min ({metrics?.response_metrics?.pending_leads || 0} pendientes)
              </p>
            </div>
          </div>
        </div>

        {/* Leads per Hour Chart */}
        <div className="bg-white rounded-2xl border border-navy-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Leads por Hora
              </h3>
              <p className="text-xs text-navy-500">Ultimas 12 horas</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-blue-500"></span>
                Historico
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-emerald-500"></span>
                Hora actual
              </span>
            </div>
          </div>
          {metrics?.leads_per_hour && (
            <LeadsPerHourChart data={metrics.leads_per_hour} />
          )}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion Funnel */}
          <div className="bg-white rounded-2xl border border-navy-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-500" />
                  Embudo de Conversion
                </h3>
                <p className="text-xs text-navy-500">Ultimos 7 dias</p>
              </div>
              {funnel && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-600">{funnel.overall_conversion}%</p>
                  <p className="text-xs text-navy-400">conversion total</p>
                </div>
              )}
            </div>
            {funnel?.funnel && <ConversionFunnel funnel={funnel.funnel} />}
          </div>

          {/* Vendor Rankings */}
          <div className="bg-white rounded-2xl border border-navy-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-navy-800 flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Ranking de Vendedores
                </h3>
                <p className="text-xs text-navy-500">Por conversiones hoy</p>
              </div>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {metrics?.vendor_rankings?.length > 0 ? (
                metrics.vendor_rankings.map((vendor, idx) => (
                  <VendorRankItem key={vendor.id} vendor={vendor} rank={idx + 1} />
                ))
              ) : (
                <div className="text-center py-8 text-navy-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Sin actividad de vendedores hoy</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lead Sources */}
        {metrics?.lead_sources?.length > 0 && (
          <div className="bg-white rounded-2xl border border-navy-100 p-6">
            <h3 className="font-semibold text-navy-800 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              Fuentes de Leads (Hoy)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metrics.lead_sources.map((source, idx) => (
                <motion.div
                  key={source.source}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-navy-50 rounded-xl p-4 text-center"
                >
                  <p className="text-2xl font-bold text-navy-800">{source.count}</p>
                  <p className="text-xs text-navy-500 mt-1 capitalize">
                    {source.source.replace(/_/g, ' ')}
                  </p>
                  <p className="text-xs text-navy-400">{source.percentage}%</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminRealTimeMetrics;
